import { PolicyPage } from './PolicyPage.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';

/**
 * The trust portal (`docs/landing/trust-portal.md`), published for the first time.
 *
 * Counsel-cleared 2026-08-08 and never routed ([B-271](../../../../docs/platform/TODO.md)).
 *
 * ⚠️ **The "Where things stand" table stays FIRST, and the source document's editorial note says
 * why in as many words:** *"Its value is that it is the first thing a reviewer reads. Moving it
 * below the strengths turns this page into every other trust page."* A reviewer looking for a reason
 * to disqualify us should find the disqualifying facts immediately rather than three scrolls down.
 * That is a layout instruction as binding as the wording, so it is recorded here where a future
 * redesign will meet it.
 *
 * ⚠️ **Every section ends by naming what is NOT true yet** — the document's stated editorial rule
 * and the whole reason it can be trusted. Dropping a "what is not built" paragraph to tighten the
 * page would remove the thing that makes the rest credible.
 *
 * ⚠️ **The ≥98% figure is phrased ONLY as an auto-merge precision gate measured against the
 * customer's own reviewed pairs**, never as blanket matching accuracy (messaging §7).
 */
export interface TrustPortalProps {
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
  onGoSecurity: () => void;
  onGoAi: () => void;
  onGoIncidents: () => void;
  onGoLegal: () => void;
}

const STANDING: { label: string; value: React.ReactNode }[] = [
  {
    label: 'SOC 2',
    value: (
      <>
        <strong>Not held.</strong> Type I targeted within 12 months of launch; controls and evidence
        automation are built in from day one so a Type II observation window can start immediately
        after. <strong>We will not claim a certificate we do not hold.</strong>
      </>
    ),
  },
  { label: 'GDPR', value: <>In scope from day one, not as a later add-on. We are a <strong>processor</strong>; DPA available.</> },
  { label: 'ISO 27001', value: <>Not held; enterprise phase, after SOC 2.</> },
  { label: 'HIPAA / PHI', value: <><strong>Out of scope.</strong> No BAA offered. Healthcare PHI workloads are declined.</> },
  { label: 'PCI DSS', value: <>Not applicable — Dedupr never handles card data; billing runs through a PCI-compliant provider.</> },
  { label: 'Penetration test', value: <>Scheduled before general availability and annually after. <strong>Not yet performed.</strong></> },
  {
    label: 'Customers / design partners',
    value: <><strong>None.</strong> Dedupr is pre-launch. No logos, no testimonials, no case-study numbers exist and none will be invented.</>,
  },
];

const CONTROLS: { area: string; what: React.ReactNode }[] = [
  { area: 'Encryption', what: <>TLS 1.2+ in transit. At rest, provider encryption plus <strong>client-side encryption with your organisation&rsquo;s own key</strong> for the highest-density classes — snapshots and raw pulls — so storage access alone yields ciphertext.</> },
  { area: 'Token custody', what: <>Your OAuth tokens are never sent to a browser, never logged, and are held under envelope encryption with the key-encryption key in a managed secrets store. Refresh runs single-writer per connection to avoid the token-family races that mask theft.</> },
  { area: 'Tenant isolation', what: <>Per-customer storage prefixes, a per-customer database, per-customer orchestration state, and <strong>single-tenant processing containers</strong> that handle one organisation&rsquo;s data per job and wipe scratch afterwards. Cross-organisation access attempts fail closed and raise a security event.</> },
  { area: 'Access control', what: <>Four roles (Owner / Admin / Reviewer / Viewer), enforced <strong>server-side on every request</strong> against a permission matrix held as data in one place, <strong>fail-closed</strong> — a mutating route the matrix does not declare is refused rather than open. The UI only hides what the API already denies.</> },
  { area: 'Internal access', what: <>No standing production access to customer data. Break-glass requires two-person approval and writes to the tamper-evident log. Our own operator console <strong>cannot read your records at all</strong> — it reads counts and account metadata, enforced by a test that pins exactly which fields may cross.</> },
  { area: 'Personnel & supply chain', what: <>Dependency scanning and secret scanning on every change; pinned lockfiles and container images; signed images deployed by digest; a minimal dependency budget in connector code, which is where tokens and raw data meet.</> },
  { area: 'Vulnerability SLAs', what: <>Critical (CVSS ≥ 9 or actively exploited) 48 h · High 7 days · Medium 30 days · Low next release.</> },
];

export function TrustPortal({
  onGoSecurity,
  onGoAi,
  onGoIncidents,
  onGoLegal,
  ...shell
}: TrustPortalProps) {
  return (
    <PolicyPage
      section="Trust"
      title="Trust & security"
      intro="Most of a security questionnaire, answered without a call — including the parts that disqualify us."
      reviewed="2026-08-08"
      counselCleared
      {...shell}
    >
      {/* ⚠️ FIRST, deliberately. See the note at the top of this file. */}
      <h2>Where things stand</h2>
      <dl className="land-legal__qa">
        {STANDING.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>

      <h2>What Dedupr does with your data</h2>
      <p>
        You connect your CRM and document systems. We read them, find duplicates and quality
        problems, and — <strong>only after one of your people approves each change</strong> — write
        corrections back or export cleaned copies to storage you own.
      </p>
      <p><strong>Four properties that are architecture rather than policy:</strong></p>
      <ol>
        <li>
          <strong>Read-only to start.</strong> Every connection requests read scopes only. Write
          permission is requested later, <strong>per action type</strong>, the first time you approve
          an action of that type. You can see exactly which scopes were granted, because we record
          what the provider actually granted rather than what we asked for.
        </li>
        <li>
          <strong>Nothing changes without a person.</strong> Not a setting — a structural rule. AI
          proposes; a human approves. The one tier that can act without a per-pair human decision
          (auto-merge) is <strong>off until it proves ≥98% precision against your own
          reviewers&rsquo; decisions</strong>, and turns itself back off if that slips.
        </li>
        <li><strong>Every change is snapshotted before it happens and reversible for 30 days.</strong> Including a merge.</li>
        <li><strong>Every action is in a hash-chained, append-only audit log you can read and export</strong> — and whose integrity you can verify yourself rather than take on trust.</li>
      </ol>

      <h2>Where your data lives</h2>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div><strong>Two locations, and the distinction is the one thing on this page worth reading twice.</strong></div>
      </div>
      <p>
        <strong>Your records</strong> — the data you connect and everything derived from it — live in{' '}
        <strong>the region you choose, EU or US</strong>, and never leave it. Storage, database,
        processing, AI calls, email and audit all pin to that region together. A customer operating in
        two regions gets one set of infrastructure per region, and records never move between them.
        Which also means: <strong>we do not deduplicate an EU record against a US one.</strong> That
        is the cost of the model, and it is the intended cost.
      </p>
      <p>
        <strong>Your account</strong> — that you are a customer, which region you chose, what you pay
        for, an <strong>opaque</strong> reference linking a person to your organisation — lives in{' '}
        <strong>one database in the United Kingdom</strong>, shared across all customers. It holds{' '}
        <strong>none of your data</strong> and <strong>no names or email addresses</strong>; the
        latter live in the sign-in service. There is no EU-versus-US customer content in it to keep
        apart. The UK holds an EU adequacy decision, so the EEA→UK element is a permitted transfer;
        our DPA also carries SCCs with the UK Addendum as a standing fallback.
      </p>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>If your policy cannot accept account metadata leaving your jurisdiction</strong>,
          the answer is a <strong>BYO-Cloud deployment</strong> — the engine runs in your own cloud
          account and only health metadata reaches us. <strong>It has never been deployed for a
          customer.</strong> We would be doing it for the first time with you, and we would rather
          say that than let you find out during implementation.
        </div>
      </div>

      <h2>Your data and AI</h2>
      <ul>
        <li><strong>AI never executes anything.</strong> It proposes; a person approves.</li>
        <li><strong>It sees the match-relevant fields of one pair</strong> — not full records, not related records, not free-text notes unless you configure a field as match evidence.</li>
        <li><strong>You can bring your own keys</strong>, in which case evidence goes only to your own approved vendor under your own agreement, with no markup — and there is no Dedupr-side AI sub-processing for you at all.</li>
        <li><strong>You can turn AI off entirely.</strong> A fully deterministic run is supported, not degraded.</li>
        <li><strong>Nobody trains on your data.</strong> Not us, and no arrangement with a provider permits it.</li>
        <li><strong>Every AI-influenced decision produces a receipt</strong> recording the model, the prompt version, the region, the cost, the verdict — and <strong>the answer that would have applied without the model</strong>, which is how you can check whether the AI changed any outcome at all. Receipts hold field <strong>names and hashes, never values</strong>.</li>
      </ul>
      <p>
        Full detail, per provider:{' '}
        <button className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoAi}>
          the AI transparency note
        </button>.
      </p>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>What is not built:</strong> Azure OpenAI and Google Gemini are declared and{' '}
          <strong>refuse by name</strong> rather than silently routing elsewhere. The prompt
          library&rsquo;s actual content and its test sets are not written. No provider has been
          called with a real key in a customer context, because there are no customers.
        </div>
      </div>

      <h2>Security controls</h2>
      <dl className="land-legal__qa">
        {CONTROLS.map((row) => (
          <div key={row.area}>
            <dt>{row.area}</dt>
            <dd>{row.what}</dd>
          </div>
        ))}
      </dl>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>What is not in place:</strong> no penetration test has been performed; there is{' '}
          <strong>no alerting wired to a person</strong> yet, so detection currently depends on
          someone looking; and staff multi-factor authentication on our internal console is
          implemented but is a go-live gate rather than a completed one. We list these because a
          reviewer will ask, and because listing them is cheaper than being caught by them.
        </div>
      </div>

      <h2>Availability</h2>
      <p><strong>We commit to 99.9% monthly availability, at every paid tier.</strong></p>
      <ul>
        <li><strong>Measured</strong> by external checks from three regions at one-minute intervals against <code>api.</code>, <code>my.</code>, <code>settings.</code> and <code>guest.</code>; a minute counts as down when <strong>two of the three</strong> fail; a 5xx from our edge counts, a 4xx does not; the window is a calendar month, UTC.</li>
        <li><strong>Excluded, and each exclusion is capped or bounded:</strong> scheduled maintenance announced 72 hours ahead and <strong>capped at 4 hours a month</strong>; your own source systems, destinations or AI provider being down; data planes we do not operate; your own network; suspension for non-payment.</li>
        <li><strong>A Cloudflare platform incident is deliberately NOT excluded.</strong> We chose the substrate; you did not.</li>
        <li><strong>No service credits, at any tier.</strong> We say this plainly rather than omitting it. A credit is a priced liability and we have not priced one.</li>
      </ul>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>The measurement is not running yet.</strong> The definition above exists so the
          commitment can be audited against something precise; the monitoring that produces the
          monthly figure is specified and not configured, and there is no status page. We would
          rather publish the definition and the gap than a number with no method behind it.
        </div>
      </div>

      <h2>Incidents</h2>
      <ul>
        <li><strong>Personal-data breach notification without undue delay and within 72 hours</strong> of our determining a breach occurred. You are the controller; we notify <strong>you</strong>, with what you need to notify your regulator and data subjects.</li>
        <li><strong>Status page within 30 minutes</strong> of a SEV-1 declaration; <strong>direct contact within 1 hour</strong> if you are affected; <strong>written post-mortem within 5 business days</strong>.</li>
        <li><strong>A cross-customer data leak is SEV-1 regardless of size.</strong></li>
        <li><strong>Containment is a rollback, not a restore.</strong> Every Dedupr-initiated write has a pre-image.</li>
        <li><strong>No incident history exists</strong>, because there are no customers.</li>
      </ul>
      <p>
        Full policy:{' '}
        <button className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoIncidents}>
          incident response &amp; breach notification
        </button>.
      </p>

      <h2>Your rights and your exit</h2>
      <ul>
        <li><strong>Export your data at any time</strong> — golden records with lineage columns and per-field PII tags, to storage you own.</li>
        <li><strong>Disconnect any source immediately.</strong> Revocation is attempted upstream and our copy is deleted either way — <strong>a failed upstream revocation still deletes our copy and tells you it failed.</strong></li>
        <li><strong>Roll back any applied action</strong> inside its 30-day window, yourself.</li>
        <li><strong>Close your organisation</strong>, with an export offered first and a full purge of your records within 30 days. A signed purge attestation is retained afterwards — it holds no field values and is the only remaining evidence the deletion happened.</li>
        <li><strong>Data subject requests</strong> are raised by your Owner or Admin in-app and answered within <strong>30 days</strong>. There is deliberately no public web form: we are the processor, and acting on a data subject&rsquo;s request without your instruction would be the opposite of that role.</li>
        <li><strong>Erasure works even against our rollback snapshots.</strong> Each snapshot has its own key; an erasure destroys the key, the content becomes unrecoverable, and the affected items are marked non-rollbackable. <strong>Erasure wins over recoverability.</strong></li>
      </ul>

      <h2>Documents</h2>
      <dl className="land-legal__qa">
        <div>
          <dt><button className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoSecurity}>Security one-pager</button></dt>
          <dd>The two-page version for a vendor-review questionnaire.</dd>
        </div>
        <div>
          <dt><button className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoLegal}>DPA &amp; sub-processor register</button></dt>
          <dd>Procurement and your DPO.</dd>
        </div>
        <div>
          <dt><button className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoAi}>AI transparency note</button></dt>
          <dd>AI governance review.</dd>
        </div>
        <div>
          <dt><button className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoIncidents}>Incident response &amp; breach notification</button></dt>
          <dd>Your security team.</dd>
        </div>
        <div>
          <dt>Evidence packs</dt>
          <dd>Generated from the product on demand, per period, with an integrity manifest — available to customers once running.</dd>
        </div>
        <div>
          <dt>SOC 2 report</dt>
          <dd><strong>Does not exist yet.</strong> See the top of this page.</dd>
        </div>
      </dl>

      <p><strong>Contact:</strong> security@dedupr.cloud · acknowledgement within 72 hours.</p>
    </PolicyPage>
  );
}
